/**
 * Revenue — high-level MRR / ARPU + plan breakdown.
 *
 * Phase-1 surface uses ``/admin/revenue`` which is a thin pass:
 * total MRR, ARPU, customer counts per plan, the same 30-day
 * trendline used on Overview. Phase 2 will add Stripe drill-downs
 * (refunds, expansion / contraction, churn cohorts) — those need
 * a deeper Stripe data feed than we have today.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getRevenue, RevenueResponse } from "@/api/admin";
import { fmtNum, fmtUsd } from "@/lib/utils";

export function RevenuePage() {
  const [data, setData] = useState<RevenueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getRevenue()
      .then((d) => !cancelled && setData(d))
      .catch((e) =>
        !cancelled && setError(e?.message || "Failed to load revenue"),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  if (error)
    return <div className="p-8 text-sm text-danger">Failed: {error}</div>;
  if (!data)
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Revenue</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          MRR snapshot + plan breakdown. Deeper Stripe drill-downs land in Phase 2.
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Total MRR" value={fmtUsd(data.total_mrr_usd)} accent />
        <Kpi label="Active customers" value={fmtNum(data.active_customers)} />
        <Kpi label="ARPU" value={fmtUsd(data.arpu_usd)} />
      </section>

      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">
          MRR trend (30d)
        </h2>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data.mrr_30d}>
            <defs>
              <linearGradient id="revMrr" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => d.slice(5)}
              fontSize={11}
              stroke="#94A3B8"
            />
            <YAxis fontSize={11} stroke="#94A3B8" />
            <Tooltip formatter={(v: number) => fmtUsd(v)} />
            <Area
              type="monotone"
              dataKey="mrr_usd"
              stroke="#6366F1"
              strokeWidth={2}
              fill="url(#revMrr)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </section>

      <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <header className="px-4 py-2.5 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">By plan</h2>
        </header>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Plan</th>
              <th className="text-right px-4 py-2 font-semibold">Customers</th>
              <th className="text-right px-4 py-2 font-semibold">MRR</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.by_plan.map((row) => (
              <tr key={row.plan}>
                <td className="px-4 py-2.5 capitalize">{row.plan}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {fmtNum(row.customers)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {fmtUsd(row.mrr_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-4 py-3.5">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${accent ? "text-accent" : "text-slate-900"}`}
      >
        {value}
      </div>
    </div>
  );
}
